import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ChevronRight, Code2, Database, Monitor, ChevronDown } from 'lucide-react';

const SCRIPTLY_API_BASE = process.env.REACT_APP_API_BASE_URL_CC;

const TopNavbar = ({
  extractInstance,
  transformInstance,
  handleGenerateSQL,
  scGenerating,
  scMappingDocs,
  scMappingDocsLoading,
  scSelectedMappings,
  setScSelectedMappings
}) => {
  const [isObjectOpen, setIsObjectOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsObjectOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleMappingSelection = (doc) => {
    const isSelected = scSelectedMappings.some(d => (d.id && d.id === doc.id) || (d._id && d._id === doc._id) || d === doc);
    if (isSelected) {
      setScSelectedMappings(scSelectedMappings.filter(d => !((d.id && d.id === doc.id) || (d._id && d._id === doc._id) || d === doc)));
    } else {
      setScSelectedMappings([...scSelectedMappings, doc]);
    }
  };

  const selectedCount = scSelectedMappings?.length || 0;
  let displayText = 'Select...';
  if (scMappingDocsLoading) {
    displayText = 'Loading...';
  } else if (selectedCount > 0) {
    const firstObj = scSelectedMappings[0];
    const name = firstObj.objectDescription || firstObj.mapping_document_name || firstObj.name || firstObj.fileName || 'Object';
    if (selectedCount === 1) {
      displayText = name;
    } else {
      displayText = `${name} +${selectedCount - 1}`;
    }
  }

  return (
    <div className="scriptly-top-navbar border-b border-slate-800/80">

      <div className="navbar-center">
        <div className="flex items-center gap-8">
          <div className="flex items-center gap-2">
            <Monitor size={14} className="text-slate-600" />
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Extract</span>
            <div className="nav-selector cursor-default pointer-events-none border-dashed border-slate-700/60 bg-transparent">
              <span className="font-semibold text-blue-400">{extractInstance}</span>
            </div>
          </div>

          <ChevronRight size={14} className="text-slate-800" />

          <div className="flex items-center gap-2">
            <Database size={14} className="text-slate-600" />
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Transform</span>
            <div className="nav-selector cursor-default pointer-events-none border-dashed border-slate-700/60 bg-transparent">
              <span className="font-semibold text-blue-400">{transformInstance}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Object:</span>
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                className="nav-selector border-dashed w-[180px] justify-between shadow-sm outline-none hover:border-blue-500/50 hover:bg-slate-50 transition-all cursor-pointer bg-[#ffffff]"
                onClick={() => !scMappingDocsLoading && setIsObjectOpen(!isObjectOpen)}
                disabled={scMappingDocsLoading}
              >
                <span className={`truncate w-full text-left ${selectedCount > 0 ? "text-blue-500 font-bold" : "text-slate-400"}`}>
                  {displayText}
                </span>
                <ChevronDown size={14} className={`text-slate-400 transition-transform duration-200 ${isObjectOpen ? "rotate-180" : ""}`} />
              </button>

              {isObjectOpen && (
                <div role="menu" className="absolute top-full left-0 mt-2 min-w-[320px] bg-[#ffffff] border border-slate-200 rounded-lg shadow-2xl z-[100] py-2 max-h-[400px] overflow-y-auto custom-scrollbar ring-1 ring-black/5">
                  {Array.isArray(scMappingDocs) && scMappingDocs.length > 0 ? (
                    scMappingDocs.map((doc, idx) => {
                      const isSelected = scSelectedMappings.some(d => (d.id && d.id === doc.id) || (d._id && d._id === doc._id) || d === doc);
                      return (
                        <div
                          key={doc.id || doc._id || idx}
                          className={`px-4 py-2.5 text-[12px] cursor-pointer transition-colors border-b border-slate-100 last:border-0 flex items-start gap-3 ${isSelected ? 'bg-blue-50 hover:bg-blue-100/50' : 'hover:bg-slate-50'}`}
                          onClick={() => toggleMappingSelection(doc)}
                          role="menuitem"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleMappingSelection(doc);
                            }
                          }}
                        >
                          <div className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-blue-500 border-blue-500' : 'border-slate-300 bg-slate-50'}`}>
                            {isSelected && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                          </div>
                          <div className="flex-1 overflow-hidden">
                            <div className={`font-semibold truncate ${isSelected ? 'text-blue-600' : 'text-slate-700'}`}>
                              {doc.objectDescription || doc.mapping_document_name || doc.name || doc.fileName || doc.document_name || `Object ${idx + 1}`}
                            </div>
                            <div className={`text-[10px] truncate ${isSelected ? 'text-blue-500/70' : 'text-slate-500'}`}>
                              {doc.fileName || doc.description || 'Select this mapping document'}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="px-4 py-4 text-[12px] text-slate-500 text-center italic">
                      {scMappingDocsLoading ? 'Loading objects...' : 'No objects found.'}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="navbar-right">
        <button
          onClick={handleGenerateSQL}
          disabled={scGenerating}
          className="gen-btn bg-blue-600 hover:bg-blue-500 shadow-[0_2px_10px_rgba(37,99,235,0.2)] active:scale-95 transition-all"
        >
          <Code2 size={15} />
          <span className="tracking-wide">Generate Script</span>
        </button>
      </div>
    </div>
  );
};

TopNavbar.propTypes = {
  extractInstance: PropTypes.string,
  transformInstance: PropTypes.string,
  handleGenerateSQL: PropTypes.func.isRequired,
  scGenerating: PropTypes.bool.isRequired,
  scMappingDocs: PropTypes.array.isRequired,
  scMappingDocsLoading: PropTypes.bool.isRequired,
  scSelectedMappings: PropTypes.array,
  setScSelectedMappings: PropTypes.func.isRequired,
};

export default TopNavbar;
